// CursorPosition.js
//
// The line,col and Top/Bot indicator in the title bar, kept honest.
//
// A rendered page has no file lines, so "line" here is the block the cursor is
// in — a paragraph, a heading, a list item — counted in document order, and
// "col" is the characters into that block. While you are editing, both follow
// the caret; the rest of the time they follow whatever is scrolled into view,
// since that is the closest thing to where you are looking.

const BLOCKS = "p, h2, h3, h4, li, pre";

export class CursorPosition {
  constructor(element) {
    this.content = element.querySelector(".window-content");
    this.pos = element.querySelector(".window-top .pos");

    this.content.addEventListener("scroll", () => this.render());
    this.content.addEventListener("input", () => this.render());
    document.addEventListener("selectionchange", () => this.render());

    // The buffer is replaced wholesale when another one is opened.
    new MutationObserver(() => this.render()).observe(this.content, {
      childList: true,
    });

    this.render();
  }

  render() {
    const [line, column] = this.caret() ?? this.firstVisible();
    this.pos.textContent = `${line},${column}   ${this.scrolled()}`;
  }

  blocks() {
    return [...this.content.querySelectorAll(BLOCKS)];
  }

  // Where the caret is, or null when it is somewhere else on the page.
  caret() {
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.anchorNode) {
      return null;
    }

    const node = selection.anchorNode;
    if (!this.content.contains(node)) {
      return null;
    }

    const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    const block = element.closest(BLOCKS);
    const blocks = this.blocks();
    if (!block || !blocks.includes(block)) {
      return null;
    }

    const upToCaret = document.createRange();
    upToCaret.selectNodeContents(block);
    upToCaret.setEnd(node, selection.anchorOffset);

    return [blocks.indexOf(block) + 1, upToCaret.toString().length + 1];
  }

  firstVisible() {
    const blocks = this.blocks();
    const top = this.content.scrollTop;
    const index = blocks.findIndex(
      (block) => block.offsetTop + block.offsetHeight > top
    );

    return [index === -1 ? 1 : index + 1, 1];
  }

  scrolled() {
    const { scrollTop, scrollHeight, clientHeight } = this.content;
    const hidden = scrollHeight - clientHeight;

    if (hidden <= 0) {
      return "All";
    }
    if (scrollTop <= 0) {
      return "Top";
    }
    if (scrollTop >= hidden - 1) {
      return "Bot";
    }

    return `${Math.round((scrollTop / hidden) * 100)}%`;
  }
}
